/* Order protection (Oro Protection). Markup: snippets/order-protection.liquid.
 * - Nothing is added on page load. Dual CTA: the line is added only when the shopper
 *   clicks the checkout button whose total includes it. Opt-in card: only when they
 *   switch it on.
 * - A protection line already in the cart is kept on the right tier, at qty 1, and
 *   removed when there is no merchandise. Sync never adds one.
 * - Every failure falls through to checkout without protection.
 * Checkout clicks reach us last: gift-cart-guard.js and bogo-confirm.js intercept the
 * click first, then submit the form, and we take the form's submit event.
 */
(function (root) {
  'use strict';

  const VENDOR = 'Order Protection';
  const SKU_PREFIX = 'JO-PROTECT-';

  function isProtectionLine(item) {
    return Boolean(item) && (item.vendor === VENDOR || String(item.sku || '').indexOf(SKU_PREFIX) === 0);
  }

  function isGiftLine(item) {
    const marker = item.properties && item.properties._auto_added;
    return marker === true || marker === 'true' || marker === 1 || marker === '1';
  }

  // Merchandise after line and cart-level discounts, in cents. Tier and threshold math use this.
  function merchSubtotal(cart) {
    let total = 0;
    for (const item of cart.items || []) {
      if (!isProtectionLine(item) && !isGiftLine(item)) total += item.final_line_price;
    }
    total -= (cart.cart_level_discount_applications || []).reduce((sum, d) => sum + d.total_allocated_amount, 0);
    return Math.max(0, total);
  }

  function tierFor(cents, tiers) {
    if (!(cents > 0)) return null;
    return (tiers || []).find((tier) => tier.max == null || cents <= tier.max) || null;
  }

  // Writes that leave exactly one line of `tier` in the cart, or none when tier is null.
  // A new line is only planned when allowAdd is set or a line is being re-tiered.
  function plan(cart, tier, { allowAdd = false } = {}) {
    const lines = (cart.items || []).filter(isProtectionLine);
    const keep = tier ? lines.find((line) => String(line.variant_id) === String(tier.id)) : null;
    const updates = {};
    for (const line of lines) {
      if (line === keep) {
        if (line.quantity !== 1) updates[line.key] = 1;
      } else {
        updates[line.key] = 0;
      }
    }
    const add = tier && !keep && (allowAdd || lines.length > 0) ? tier : null;
    return { updates, add };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { isProtectionLine, merchSubtotal, tierFor, plan };
  }

  const config = root.JamesOroProtection;
  if (!config || config.ready || typeof document === 'undefined') return;
  config.ready = true;
  config.isProtectionLine = isProtectionLine;
  config.merchSubtotal = merchSubtotal;
  config.setMode = function (mode) {
    if (mode !== 'dual_cta' && mode !== 'optin_card') return;
    config.mode = mode;
    document.documentElement.dataset.opMode = mode;
  };

  const cartRoot = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
  const LIMIT_MS = 8000;

  function mode() {
    return document.documentElement.dataset.opMode === 'optin_card' ? 'optin_card' : 'dual_cta';
  }

  function money(cents) {
    const value = cents / 100;
    return '$' + (cents % 100 === 0 ? value.toLocaleString('en-US') : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  }

  // Goes through window.fetch on purpose: gift-cart-guard.js wraps it and must see our writes.
  async function request(path, body) {
    const response = await root.fetch(cartRoot + path, body
      ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }
      : { cache: 'no-store' });
    if (!response.ok) throw new Error(`${path} ${response.status}`);
    return response.json();
  }

  function currentTier(cart) {
    return config.enabled ? tierFor(merchSubtotal(cart), config.tiers) : null;
  }

  // Returns true when a protection line was added, removed or changed.
  async function apply(cart, { allowAdd = false, attributes = null } = {}) {
    const tier = currentTier(cart);
    const next = plan(cart, tier, { allowAdd });
    if (next.add) {
      await request('cart/add.js', {
        items: [{
          id: Number(next.add.id),
          quantity: 1,
          properties: {
            _protection_tier: String(next.add.price),
            _protection_basis: (merchSubtotal(cart) / 100).toFixed(2)
          }
        }]
      });
    }
    const hasUpdates = Object.keys(next.updates).length > 0;
    if (hasUpdates || attributes) {
      await request('cart/update.js', Object.assign({}, hasUpdates ? { updates: next.updates } : {}, attributes ? { attributes } : {}));
    }
    return Boolean(next.add) || hasUpdates;
  }

  async function removeAll(cart, attributes) {
    const updates = {};
    (cart.items || []).filter(isProtectionLine).forEach((line) => { updates[line.key] = 0; });
    const hasUpdates = Object.keys(updates).length > 0;
    await request('cart/update.js', Object.assign({}, hasUpdates ? { updates } : {}, { attributes }));
    return hasUpdates;
  }

  // Redraw the drawer (and /cart) after our own write. /cart swaps refreshCartSections
  // for a quiet page refresh; elsewhere the drawer redraws itself without reopening.
  function refreshViews() {
    if (document.querySelector('[data-jo-cart]') && typeof root.refreshCartSections === 'function') {
      return Promise.resolve(root.refreshCartSections()).catch(() => {});
    }
    const items = document.querySelector('cart-drawer-items');
    if (items && typeof items.onCartUpdate === 'function') {
      return Promise.resolve(items.onCartUpdate({ source: 'order-protection' })).catch(() => {});
    }
    return Promise.resolve();
  }

  function liveRegion() {
    return document.getElementById('CartDrawer-LiveRegionText') || document.getElementById('cart-live-region-text');
  }

  function announce(message) {
    const region = liveRegion();
    if (!region || !message) return;
    region.setAttribute('aria-hidden', 'false');
    region.textContent = message;
  }

  /* ---------- Checkout ---------- */

  let leaving = false;

  function setBusy(busy) {
    document.querySelectorAll('[data-op-accept], [data-op-decline]').forEach((button) => {
      button.disabled = busy;
      button.classList.toggle('is-loading', busy);
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    });
  }

  function withLimit(promise) {
    return Promise.race([promise, new Promise((resolve, reject) => setTimeout(() => reject(new Error('timed out')), LIMIT_MS))]);
  }

  async function checkout(kind) {
    leaving = true;
    setBusy(true);
    try {
      await withLimit((async () => {
        const cart = await request('cart.js');
        if (kind === 'decline') {
          await removeAll(cart, { _protection_choice: 'declined' });
        } else if (mode() === 'dual_cta') {
          await apply(cart, { allowAdd: true, attributes: { _protection_choice: 'accepted' } });
        } else {
          // Opt-in card: the switch already added or removed the line. Just make sure the tier is current.
          await apply(cart);
        }
      })());
    } catch (err) {
      console.warn('[OP]', err);
    }
    root.location.href = cartRoot + 'checkout';
  }

  document.addEventListener('submit', (event) => {
    const submitter = event.submitter;
    if (!submitter || event.defaultPrevented) return;
    const kind = submitter.matches('[data-op-decline]') ? 'decline' : submitter.matches('[data-op-accept]') ? 'accept' : null;
    if (!kind) return;
    event.preventDefault();
    if (!leaving) checkout(kind);
  });

  // Enter in a drawer quantity box would submit the form through the default button,
  // which is now the one that adds protection. Update the quantity instead (as /cart does).
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !event.target.matches || !event.target.matches('#CartDrawer-Form .quantity__input')) return;
    if (!document.querySelector('#CartDrawer-Checkout[data-op-accept]')) return;
    event.preventDefault();
    event.target.blur();
  }, true);

  /* ---------- Opt-in card ---------- */

  let toggling = false;

  async function toggle(button) {
    if (toggling) return;
    toggling = true;
    const turnOn = button.getAttribute('aria-checked') !== 'true';
    const context = button.closest('.op--page') ? 'page' : 'drawer';
    const card = button.closest('.op__card');
    const module = button.closest('[data-op]');
    button.setAttribute('aria-checked', String(turnOn));
    if (card) card.classList.toggle('is-on', turnOn);
    button.setAttribute('aria-busy', 'true');
    // Show the new state now; the redraw below confirms it from the server.
    if (module) {
      const fee = Number(module.dataset.opFee) || 0;
      const cents = Number(module.dataset.opCents) || 0;
      const note = module.querySelector('.op__note');
      if (note) note.textContent = turnOn ? 'Added' : 'Optional';
      document.querySelectorAll('.op-amount[data-op-mode-only="optin_card"]').forEach((amount) => {
        amount.textContent = ` · ${money(turnOn ? cents + fee : cents)}`;
      });
    }
    try {
      const cart = await request('cart.js');
      if (turnOn) await apply(cart, { allowAdd: true, attributes: { _protection_choice: 'accepted' } });
      else await removeAll(cart, { _protection_choice: 'declined' });
      const merch = merchSubtotal(cart);
      const tier = currentTier(cart);
      await refreshViews();
      if (tier) {
        announce(turnOn
          ? `${config.name} added, ${money(tier.price)}. Total ${money(merch + tier.price)}.`
          : `${config.name} removed. Total ${money(merch)}.`);
      }
    } catch (err) {
      console.warn('[OP]', err);
      await refreshViews();
    } finally {
      toggling = false;
      const next = document.querySelector(`.op--${context} [data-op-toggle]`);
      if (next) {
        next.removeAttribute('aria-busy');
        if (!document.activeElement || document.activeElement === document.body || !document.activeElement.isConnected) next.focus();
      }
    }
  }

  /* ---------- Info panel ---------- */

  let openPanel = null;
  let opener = null;
  let swallowEscapeUp = false;

  function setExpanded(panel, expanded) {
    document.querySelectorAll(`[data-op-info][aria-controls="${panel.id}"]`).forEach((button) => {
      button.setAttribute('aria-expanded', String(expanded));
    });
  }

  function showPanel(button) {
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;
    if (openPanel && openPanel !== panel) hidePanel(false);
    panel.hidden = false;
    setExpanded(panel, true);
    openPanel = panel;
    opener = button;
    const close = panel.querySelector('[data-op-info-close]');
    (close || panel).focus();
  }

  function hidePanel(returnFocus) {
    if (!openPanel) return;
    openPanel.hidden = true;
    setExpanded(openPanel, false);
    openPanel = null;
    if (returnFocus && opener && opener.isConnected) opener.focus();
    opener = null;
  }

  document.addEventListener('click', (event) => {
    const switchButton = event.target.closest('[data-op-toggle]');
    if (switchButton) {
      event.preventDefault();
      toggle(switchButton);
      return;
    }
    const info = event.target.closest('[data-op-info]');
    if (info) {
      event.preventDefault();
      if (openPanel && openPanel.id === info.getAttribute('aria-controls')) hidePanel(true);
      else showPanel(info);
      return;
    }
    if (event.target.closest('[data-op-info-close]')) {
      hidePanel(true);
      return;
    }
    if (openPanel && !event.target.closest('.op__panel')) hidePanel(false);
  });

  // The drawer closes on Escape keyup. Close only the panel when it is open.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !openPanel) return;
    event.stopPropagation();
    swallowEscapeUp = true;
    hidePanel(true);
  }, true);
  document.addEventListener('keyup', (event) => {
    if (event.key !== 'Escape' || !swallowEscapeUp) return;
    swallowEscapeUp = false;
    event.stopPropagation();
  }, true);

  /* ---------- Sync an existing line ---------- */

  let timer = null;
  let running = false;
  let again = false;
  let streak = 0;
  let lastWrite = 0;
  let lastTotal = null;

  function schedule(event) {
    if (event && event.detail && event.detail.source === 'order-protection') return;
    if (leaving) return;
    clearTimeout(timer);
    timer = setTimeout(sync, 300);
  }

  function announceTotal(cart) {
    if (!document.querySelector('[data-op]')) return;
    const merch = merchSubtotal(cart);
    const tier = currentTier(cart);
    const hasLine = (cart.items || []).some(isProtectionLine);
    const withFee = mode() === 'dual_cta' || hasLine;
    const total = tier && withFee ? merch + tier.price : merch;
    if (lastTotal !== null && total !== lastTotal && tier) {
      announce(withFee
        ? `Checkout total ${money(total)}, includes ${config.name} ${money(tier.price)}.`
        : `Checkout total ${money(total)}.`);
    }
    lastTotal = total;
  }

  async function sync() {
    if (leaving) return;
    if (running) { again = true; return; }
    running = true;
    try {
      // Two writes in a row without the cart settling means something is fighting us
      // (the gift guard, an app). Stop until the next change.
      if (Date.now() - lastWrite > 5000) streak = 0;
      const cart = await request('cart.js');
      if (!(cart.items || []).some(isProtectionLine)) {
        announceTotal(cart);
        return;
      }
      if (streak >= 2) return;
      const wrote = await apply(cart);
      if (wrote) {
        streak += 1;
        lastWrite = Date.now();
        await refreshViews();
      } else {
        streak = 0;
        announceTotal(cart);
      }
    } catch (err) {
      console.warn('[OP]', err);
    } finally {
      running = false;
      if (again) { again = false; schedule(); }
    }
  }

  document.addEventListener('gift-cart:updated', schedule);
  document.addEventListener('cart:updated', schedule);
  document.addEventListener('cart:refresh', schedule);
  root.addEventListener('ig:CartUpdated', schedule);
  root.addEventListener('pageshow', (event) => {
    // Back from checkout through the back/forward cache: the page is restored mid-redirect.
    if (event.persisted) {
      leaving = false;
      setBusy(false);
    }
    schedule();
  });
  // pubsub.js loads after this file, so subscribe once the document is parsed.
  let started = false;
  function start() {
    if (started) return;
    started = true;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
    }
    schedule();
  }
  if (document.readyState === 'complete') start();
  else {
    document.addEventListener('DOMContentLoaded', start);
    root.addEventListener('load', start);
  }
})(typeof window !== 'undefined' ? window : globalThis);
